
        <div class="space-y-8 pb-12">
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 class="text-4xl font-bold tracking-tight text-[color:var(--foreground)]">Properties Management</h1>
                    <p class="mt-2 text-lg text-[color:var(--muted-foreground)]">View and manage your property listings</p>
                </div>
                <div class="flex flex-wrap items-center gap-3">
                    <label class="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700">
                        <lucide-icon name="folder-kanban" class="h-4 w-4 text-slate-500"></lucide-icon>
                        <select
                            [(ngModel)]="filters.projectId"
                            (change)="onFiltersChange()"
                            aria-label="Project to export"
                            class="max-w-[13rem] bg-transparent text-sm font-semibold text-slate-900 focus:outline-none"
                        >
                            <option value="">View projects</option>
                            <option *ngFor="let project of projectsForFilter" [value]="project.id">{{ project.name }}</option>
                        </select>
                    </label>
                    <app-export-report-menu [disabled]="exporting || loadingPage" [exportingFormat]="exportingFormat" (exportFormat)="exportProperties($event)"></app-export-report-menu>
                    <button
                        *ngIf="canBulkImportProperties"
                        type="button"
                        (click)="openBulkImport()"
                        class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 font-semibold text-[color:var(--foreground)] shadow-sm transition-colors hover:bg-[color:var(--hover)]"
                    >
                        <lucide-icon name="upload-cloud" class="h-5 w-5"></lucide-icon>
                        Bulk upload units
                    </button>
                    <button
                        *ngIf="canBulkEditPrices"
                        type="button"
                        (click)="openBulkPriceEditor()"
                        [disabled]="bulkPriceLoading || bulkPriceSubmitting"
                        class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 font-semibold text-[color:var(--foreground)] shadow-sm transition-colors hover:bg-[color:var(--hover)] disabled:opacity-60"
                    >
                        <lucide-icon [name]="bulkPriceLoading ? 'loader-2' : 'wallet'" class="h-5 w-5" [class.animate-spin]="bulkPriceLoading"></lucide-icon>
                        Bulk edit prices
                    </button>
                    <a *ngIf="canCreateProperties" routerLink="/developer/properties/create" queryParamsHandling="preserve" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 font-semibold text-[color:var(--primary-foreground)] shadow-sm transition-colors hover:bg-[color:var(--primary-hover)]">
                        <lucide-icon name="upload" class="h-5 w-5"></lucide-icon>
                        Add Property
                    </a>
                </div>
            </div>

            <div class="relative">
                <div #filterSentinel aria-hidden="true" class="h-px w-full"></div>
                <div
                    class="sticky z-40"
                    [appStableStickyFilter]="isStickyCompact"
                    [style.overflow-anchor]="'none'"
                    [style.top.px]="stickyTopOffsetPx"
                >
                    <div class="rounded-2xl border border-slate-200 bg-white shadow-sm"
                         [class.rounded-t-none]="isFilterStuck" [class.border-t-0]="isFilterStuck"
                    >
                    <div class="p-4 lg:p-5" [ngClass]="isStickyCompact ? 'space-y-2' : 'space-y-4'">
                        <div *ngIf="!isStickyCompact" class="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                            <div class="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 flex-1">
                                <lucide-icon name="search" class="h-5 w-5 text-[color:var(--text-subtle)]"></lucide-icon>
                                <input
                                    type="text"
                                    [(ngModel)]="filters.search"
                                    (ngModelChange)="onFiltersChange()"
                                    placeholder="Search by unit number, title, location, or keywords..."
                                    class="w-full bg-transparent text-[color:var(--foreground)] placeholder:text-[color:var(--text-subtle)] focus:outline-none"
                                />
                            </div>
                            <button
                                type="button"
                                (click)="toggleFilters()"
                                class="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                            >
                                <lucide-icon name="filter" class="h-4 w-4 text-slate-700"></lucide-icon>
                                <span>{{ showFilters ? 'Hide filters' : 'More filters' }}</span>
                            </button>
                        </div>

                        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div class="flex flex-nowrap items-center gap-2 overflow-x-auto lg:flex-wrap lg:overflow-visible">
                                <button
                                    *ngIf="isStickyCompact"
                                    type="button"
                                    (click)="toggleFilters()"
                                    class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50"
                                >
                                    <lucide-icon name="sliders-horizontal" class="h-4 w-4"></lucide-icon>
                                </button>
                                <div class="min-w-[12rem]">
                                    <app-country-selector [(ngModel)]="filters.country" [allowEmpty]="true" [disableAutoDetect]="true" (ngModelChange)="onFiltersChange()"></app-country-selector>
                                </div>
                                <select
                                    [(ngModel)]="filters.projectId"
                                    (change)="onFiltersChange()"
                                    class="min-w-[12rem] text-sm font-semibold text-slate-900 focus:outline-none focus:border-[color:var(--ring)]"
                                    [ngClass]="isStickyCompact ? 'h-10 rounded-full border border-slate-200 bg-white px-4' : 'h-11 rounded-xl border border-slate-200 bg-white px-3'"
                                >
                                    <option value="">{{ projectsLoading ? 'Loading projects…' : 'Any project' }}</option>
                                    <option *ngFor="let project of projectsForFilter" [value]="project.id">{{ project.name }}</option>
                                </select>
                                <select
                                    [(ngModel)]="filters.status"
                                    (change)="onFiltersChange()"
                                    class="text-sm font-semibold text-slate-900 focus:outline-none focus:border-[color:var(--ring)]"
                                    [ngClass]="isStickyCompact ? 'h-10 rounded-full border border-slate-200 bg-white px-4' : 'h-11 rounded-xl border border-slate-200 bg-white px-3'"
                                >
                                    <option value="any">Any status</option>
                                    <option *ngFor="let option of propertyStatusFilterOptions" [value]="option.value">{{ option.label }}</option>
                                </select>
                                <select
                                    [(ngModel)]="filters.sortBy"
                                    (change)="onFiltersChange()"
                                    class="text-sm font-semibold text-slate-900 focus:outline-none focus:border-[color:var(--ring)]"
                                    [ngClass]="isStickyCompact ? 'h-10 rounded-full border border-slate-200 bg-white px-4' : 'h-11 rounded-xl border border-slate-200 bg-white px-3'"
                                >
                                    <option value="recent">Newest</option>
                                    <option value="price-low">Price: Low to High</option>
                                    <option value="price-high">Price: High to Low</option>
                                    <option value="ppsqm-low">Price / m²: Low to High</option>
                                    <option value="ppsqm-high">Price / m²: High to Low</option>
                                    <option value="bedrooms">Bedrooms</option>
                                    <option value="bathrooms">Bathrooms</option>
                                    <option value="unit-type">Property Type</option>
                                    <option value="title">Alphabetical</option>
                                </select>
                                <select
                                    [(ngModel)]="filters.propertyType"
                                    (change)="onFiltersChange()"
                                    class="text-sm font-semibold text-slate-900 focus:outline-none focus:border-[color:var(--ring)]"
                                    [ngClass]="isStickyCompact ? 'h-10 rounded-full border border-slate-200 bg-white px-4' : 'h-11 rounded-xl border border-slate-200 bg-white px-3'"
                                >
                                    <option *ngFor="let opt of propertyTypeOptions" [value]="opt.value">{{ opt.label }}</option>
                                </select>
                            </div>
                        </div>

                        <div *ngIf="filters.projectId && filters.country" class="mt-2 text-xs text-slate-600">
                            Country filter is ignored when a project is selected.
                        </div>

                        <div *ngIf="!isStickyCompact" class="flex flex-wrap items-center justify-between gap-3 px-1 text-sm text-[color:var(--muted-foreground)]">
                            <span>{{ resultsSummary() }}</span>
                            <span *ngIf="usesClientOnlyFilters()" class="text-xs text-[color:var(--text-subtle)]">
                                Price, feature, and tag filters evaluate the full matching dataset before counting results.
                            </span>
                        </div>
                    </div>

                    <ng-template #advancedFiltersFields>
                    <div class="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
                        <button
                            type="button"
                            (click)="filterTab = 'general'"
                            class="flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition"
                            [ngClass]="filterTab === 'general' ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-slate-700 hover:bg-slate-50'"
                        >
                            General
                        </button>
                        <button
                            type="button"
                            (click)="filterTab = 'location'"
                            class="flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition"
                            [ngClass]="filterTab === 'location' ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-slate-700 hover:bg-slate-50'"
                        >
                            Location
                        </button>
                    </div>

                    <ng-container *ngIf="filterTab === 'general'; else locationTab">
                        <div *ngIf="availableTags.length" class="flex flex-wrap gap-2">
                            <button
                                *ngFor="let tag of availableTags"
                                type="button"
                                (click)="toggleTag(tag)"
                                [ngClass]="filters.tags.includes(tag) ? 'bg-[color:var(--primary)] border-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'"
                                class="rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors"
                            >
                                + {{ tag }}
                            </button>
                        </div>

                        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Bedrooms
                                <select [(ngModel)]="filters.bedrooms" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option [ngValue]="null">Any</option>
                                    <option *ngFor="let bed of bedroomOptions" [ngValue]="bed">{{ bed }}</option>
                                </select>
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Bathrooms
                                <select [(ngModel)]="filters.bathrooms" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option [ngValue]="null">Any</option>
                                    <option *ngFor="let bath of bathroomOptions" [ngValue]="bath">{{ bath }}</option>
                                </select>
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Status
                                <select [(ngModel)]="filters.status" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option value="any">Any</option>
                                    <option *ngFor="let option of propertyStatusFilterOptions" [value]="option.value">{{ option.label }}</option>
                                </select>
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Country
                                <app-country-selector [(ngModel)]="filters.country" [allowEmpty]="true" [disableAutoDetect]="true" (ngModelChange)="onFiltersChange()"></app-country-selector>
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Sort By
                                <select [(ngModel)]="filters.sortBy" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option value="price-low">Price: Low to High</option>
                                    <option value="price-high">Price: High to Low</option>
                                    <option value="ppsqm-low">Price / sqm: Low to High</option>
                                    <option value="ppsqm-high">Price / sqm: High to Low</option>
                                    <option value="recent">Newest</option>
                                    <option value="bedrooms">Bedrooms</option>
                                    <option value="bathrooms">Bathrooms</option>
                                    <option value="unit-type">Property Type</option>
                                    <option value="title">Alphabetical</option>
                                </select>
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Min Price ({{ currencyService.currentCurrency() }})
                                <input type="number" [(ngModel)]="filters.minPrice" (change)="onFiltersChange()" class="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]" placeholder="0" />
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Max Price ({{ currencyService.currentCurrency() }})
                                <input type="number" [(ngModel)]="filters.maxPrice" (change)="onFiltersChange()" class="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]" placeholder="No limit" />
                            </label>
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Property Type
                                <select [(ngModel)]="filters.propertyType" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option *ngFor="let opt of propertyTypeOptions" [value]="opt.value">{{ opt.label }}</option>
                                </select>
                            </label>
                            <div class="flex flex-col space-y-2 text-sm text-slate-700 md:col-span-2 lg:col-span-4">
                                <span>Features</span>
                                <div class="flex flex-wrap gap-3">
                                    <label class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50">
                                        <input type="checkbox" [(ngModel)]="filters.featureGarden" (change)="onFiltersChange()" />
                                        Garden
                                    </label>
                                    <label class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50">
                                        <input type="checkbox" [(ngModel)]="filters.featureTerrace" (change)="onFiltersChange()" />
                                        Terrace
                                    </label>
                                    <label class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50">
                                        <input type="checkbox" [(ngModel)]="filters.featureGroundFloor" (change)="onFiltersChange()" />
                                        Ground floor
                                    </label>
                                </div>
                            </div>
                        </div>
                    </ng-container>

                    <ng-template #locationTab>
                        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Location
                                <input
                                    type="text"
                                    [(ngModel)]="filters.location"
                                    (change)="onFiltersChange()"
                                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]"
                                    placeholder="City, area, or landmark..."
                                />
                            </label>

                            <label class="flex flex-col space-y-2 text-sm text-slate-700">
                                Project
                                <select [(ngModel)]="filters.projectId" (change)="onFiltersChange()" class="rounded-lg border border-slate-200 bg-white px-3 py-3 text-slate-900 focus:outline-none focus:border-[#6e9bad]">
                                    <option value="">Any</option>
                                    <option *ngFor="let project of projectsForFilter" [value]="project.id">{{ project.name }}</option>
                                </select>
                                <span *ngIf="projectsLoading" class="text-xs text-slate-500">Loading projects…</span>
                                <span *ngIf="!projectsLoading && projectsError" class="text-xs text-red-600">{{ projectsError }}</span>
                            </label>
                        </div>
                    </ng-template>
                </ng-template>

                <div *ngIf="showFilters && !isMobileViewport" class="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <ng-container *ngTemplateOutlet="advancedFiltersFields"></ng-container>
                </div>
                </div>
            </div>

            <ng-container *ngIf="!loading; else loadingTpl">
                <div *ngIf="filteredProperties.length; else empty" class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <app-property-card
                        *ngFor="let property of filteredProperties"
                        [property]="property"
                        detailMode="developer"
                        [showSendOfferButton]="true"
                        (sendOffer)="openSendOffer($event)"
                    ></app-property-card>
                </div>

                <div *ngIf="hasNextPage" class="mt-10 flex justify-center">
                    <button
                        type="button"
                        (click)="showMore()"
                        [disabled]="loadingPage"
                        class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] disabled:opacity-40"
                    >
                        <lucide-icon *ngIf="loadingPage" name="loader-2" class="h-4 w-4 animate-spin"></lucide-icon>
                        <span>{{ loadingPage ? 'Loading…' : 'Show more' }}</span>
                    </button>
                </div>
            </ng-container>

            <ng-template #loadingTpl>
                <div class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div *ngFor="let i of [0,1,2,3,4,5]" class="flex flex-col overflow-hidden rounded-[28px] border border-[color:var(--border)] bg-[color:var(--card)]">
                        <div class="relative w-full aspect-video bg-slate-200 animate-pulse"></div>
                        <div class="flex flex-1 flex-col p-6 gap-4">
                            <div class="space-y-3">
                                <div class="h-5 bg-slate-200 rounded animate-pulse w-3/4"></div>
                                <div class="space-y-1.5">
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-1/4"></div>
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-full"></div>
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-5/6"></div>
                                </div>
                            </div>
                            <div class="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[color:var(--border)] pt-3">
                                <div class="h-3 bg-slate-200 rounded animate-pulse w-12"></div>
                                <div class="h-3 bg-slate-200 rounded animate-pulse w-12"></div>
                                <div class="h-3 bg-slate-200 rounded animate-pulse w-16"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </ng-template>

            <ng-template #empty>
                <div class="mt-6 rounded-2xl border border-[color:var(--border)] bg-[color:var(--muted)] p-10 text-center">
                    <lucide-icon name="building-2" class="mx-auto mb-4 h-14 w-14 text-[color:var(--text-subtle)] opacity-40"></lucide-icon>
                    <h3 class="text-xl font-semibold mb-2 text-[color:var(--foreground)]">No properties match your filters</h3>
                    <p class="mb-4 text-[color:var(--muted-foreground)]">{{ canCreateProperties ? 'Adjust your search or add a new property.' : 'Adjust your search and try again.' }}</p>
	                    <a *ngIf="canCreateProperties" routerLink="/developer/properties/create" queryParamsHandling="preserve" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
	                        <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
	                        Add Property
                    </a>
                </div>
            </ng-template>

            <div
                *ngIf="showFilters && isMobileViewport"
                class="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/40 p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur-sm lg:hidden sm:items-center sm:p-4"
            >
                <div class="flex max-h-[calc(100vh-1rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:max-h-[calc(100vh-2rem)]">
                    <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                        <p class="text-sm font-semibold text-slate-900">Filters</p>
                        <button type="button" (click)="closeFilters()" class="rounded-full bg-slate-100 p-2 text-slate-700 hover:bg-slate-200">
                            <lucide-icon name="x" class="h-4 w-4"></lucide-icon>
                        </button>
                    </div>
                    <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
                        <div class="grid grid-cols-1 gap-3">
                            <ng-container *ngTemplateOutlet="advancedFiltersFields"></ng-container>
                        </div>
                    </div>
                    <div class="border-t border-slate-200 p-4">
                        <button type="button" (click)="closeFilters()" class="w-full rounded-lg bg-[color:var(--primary)] px-4 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
                            Apply filters
                        </button>
                    </div>
                </div>
            </div>

            <!-- Bulk import modal -->
            <div
                *ngIf="bulkImportOpen"
                class="fixed inset-0 z-[120] flex items-end justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:items-center sm:p-6"
                (click)="closeBulkImport()"
            >
                <div
                    class="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] shadow-2xl sm:max-h-[calc(100dvh-3rem)]"
                    (click)="$event.stopPropagation()"
                >
                    <div class="flex items-center justify-between border-b border-[color:var(--border)] px-5 py-4">
                        <div class="min-w-0">
                            <p class="text-lg font-semibold text-[color:var(--foreground)]">Bulk upload units</p>
                            <p class="mt-1 text-xs text-[color:var(--muted-foreground)]">
                                Upload an XLSX/CSV file to safely upsert units. Reserved rows can create leads automatically.
                            </p>
                        </div>
                        <button
                            type="button"
                            (click)="closeBulkImport()"
                            class="rounded-full bg-[color:var(--muted)] p-2 text-[color:var(--foreground)] hover:bg-[color:var(--hover)]"
                            [disabled]="bulkImportPreviewing || bulkImportApplying"
                        >
                            <lucide-icon name="x" class="h-4 w-4"></lucide-icon>
                        </button>
                    </div>

                    <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
                        <div class="flex flex-wrap items-center gap-2 text-xs text-[color:var(--muted-foreground)]">
                            <span
                                class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--border)]"
                                [ngClass]="bulkImportStep === 1 ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : ''"
                            >
                                1
                            </span>
                            <span class="font-semibold" [ngClass]="bulkImportStep === 1 ? 'text-[color:var(--foreground)]' : ''">Configure</span>
                            <span class="mx-1 text-[color:var(--text-subtle)]">→</span>
                            <span
                                class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--border)]"
                                [ngClass]="bulkImportStep === 2 ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : ''"
                            >
                                2
                            </span>
                            <span class="font-semibold" [ngClass]="bulkImportStep === 2 ? 'text-[color:var(--foreground)]' : ''">Preview & apply</span>
                        </div>

                        <p *ngIf="bulkImportError" class="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
                            {{ bulkImportError }}
                        </p>

                        <ng-container *ngIf="bulkImportStep === 1">
                            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <label class="flex flex-col gap-2 text-sm text-[color:var(--muted-foreground)]">
                                    Project
                                    <select
                                        [(ngModel)]="bulkImportForm.project_id"
                                        (change)="onBulkImportProjectChanged()"
                                        [disabled]="projectsLoading || bulkImportPreviewing || bulkImportApplying"
                                        class="rounded-xl bg-[color:var(--card)] border border-[color:var(--input)] text-[color:var(--foreground)] px-3 py-3 focus:outline-none focus:border-[color:var(--ring)]"
                                    >
                                        <option value="">Select project…</option>
                                        <option *ngFor="let p of projectsForFilter" [value]="p.id">{{ p.name }}</option>
                                    </select>
                                    <span *ngIf="projectsLoading" class="text-xs text-[color:var(--text-subtle)]">Loading projects…</span>
                                    <span *ngIf="projectsError" class="text-xs text-red-600">{{ projectsError }}</span>
                                </label>

                                <label class="flex flex-col gap-2 text-sm text-[color:var(--muted-foreground)]">
                                    Currency
                                    <select
                                        [(ngModel)]="bulkImportForm.currency_code"
                                        [disabled]="bulkImportPreviewing || bulkImportApplying"
                                        class="rounded-xl bg-[color:var(--card)] border border-[color:var(--input)] text-[color:var(--foreground)] px-3 py-3 focus:outline-none focus:border-[color:var(--ring)]"
                                    >
                                        <option *ngFor="let c of bulkImportCurrencyOptions" [value]="c">{{ c }}</option>
                                    </select>
                                </label>
                            </div>

                            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <label class="flex flex-col gap-2 text-sm text-[color:var(--muted-foreground)]">
                                    Country
                                    <app-country-selector
                                        [(ngModel)]="bulkImportForm.country_code"
                                        [disabled]="bulkImportPreviewing || bulkImportApplying"
                                        [disableAutoDetect]="true"
                                    ></app-country-selector>
                                    <span class="text-xs text-[color:var(--text-subtle)]">If empty, pick the project country (2-letter code).</span>
                                </label>

                                <label class="flex items-center gap-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-3 text-sm text-[color:var(--foreground)]">
                                    <input type="checkbox" [(ngModel)]="bulkImportForm.create_leads_for_reserved" [disabled]="bulkImportPreviewing || bulkImportApplying" />
                                    Create leads for Reserved rows
                                </label>
                            </div>

                            <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-3 text-sm text-[color:var(--muted-foreground)]">
                                <p class="font-semibold text-[color:var(--foreground)]">Templates</p>
                                <p class="mt-1">
                                    The updated single-sheet format can carry title, area fields, features, garden/roof/terrace flags, up to 6 landmarks, and payment-plan columns in the same row.
                                </p>
                                <p class="mt-1 text-xs text-[color:var(--text-subtle)]">
                                    Examples: <code>Feature 1</code>, <code>Landmark 1 Name</code>, <code>Landmark 1 Distance</code>, <code>Payment Plan 1 Total Period</code>, and the matching plan fields.
                                </p>
                                <p class="mt-1">Download a starter file (sanitized, with example rows):</p>
                                <div class="mt-2 flex flex-wrap gap-3">
                                    <a
                                        href="/app/assets/templates/bulk-units-template.xlsx"
                                        download
                                        class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]"
                                    >
                                        <lucide-icon name="download" class="h-4 w-4"></lucide-icon>
                                        Download XLSX
                                    </a>
                                    <a
                                        href="/app/assets/templates/bulk-units-template.csv"
                                        download
                                        class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]"
                                    >
                                        <lucide-icon name="download" class="h-4 w-4"></lucide-icon>
                                        Download CSV
                                    </a>
                                </div>
                            </div>
                        </ng-container>

                        <ng-container *ngIf="bulkImportStep === 2">
                            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <label class="flex flex-col gap-2 text-sm text-[color:var(--muted-foreground)]">
                                    File (.xlsx or .csv)
                                    <input
                                        type="file"
                                        accept=".xlsx,.csv"
                                        (change)="onBulkImportFileChange($event)"
                                        [disabled]="bulkImportPreviewing || bulkImportApplying"
                                        class="rounded-xl border border-[color:var(--input)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] file:mr-3 file:rounded-lg file:border-0 file:bg-[color:var(--muted)] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-[color:var(--foreground)] hover:file:bg-[color:var(--hover)]"
                                    />
                                    <span *ngIf="bulkImportFileName" class="text-xs text-[color:var(--text-subtle)]">Selected: {{ bulkImportFileName }}</span>
                                </label>

                                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-3 text-sm text-[color:var(--muted-foreground)]">
                                    <p class="font-semibold text-[color:var(--foreground)]">Import settings</p>
                                    <p class="mt-1 truncate">Project: {{ selectedBulkImportProject?.name || '—' }}</p>
                                    <p>Country: {{ bulkImportForm.country_code || '—' }}</p>
                                    <p>Currency: {{ bulkImportForm.currency_code }}</p>
                                    <p>Leads for Reserved: {{ bulkImportForm.create_leads_for_reserved ? 'Yes' : 'No' }}</p>
                                </div>
                            </div>

                            <div class="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    (click)="runBulkImportPreview()"
                                    [disabled]="!canRunBulkImportPreview() || bulkImportPreviewing || bulkImportApplying"
                                    class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60"
                                >
                                    <lucide-icon [name]="bulkImportPreviewing ? 'loader-2' : 'sparkles'" class="h-4 w-4" [class.animate-spin]="bulkImportPreviewing"></lucide-icon>
                                    {{ bulkImportPreviewing ? 'Previewing…' : 'Run preview' }}
                                </button>

                                <button
                                    type="button"
                                    (click)="applyBulkImport()"
                                    [disabled]="!canApplyBulkImport() || bulkImportApplying || bulkImportPreviewing"
                                    class="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-emerald-500/20 disabled:opacity-60"
                                >
                                    <lucide-icon [name]="bulkImportApplying ? 'loader-2' : 'check-circle'" class="h-4 w-4" [class.animate-spin]="bulkImportApplying"></lucide-icon>
                                    {{ bulkImportApplying ? 'Applying…' : 'Apply import' }}
                                </button>
                            </div>

                            <div *ngIf="bulkImportPreviewResult" class="space-y-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-4 text-sm text-[color:var(--muted-foreground)]">
                                <div class="flex flex-wrap items-center justify-between gap-2">
                                    <p class="font-semibold text-[color:var(--foreground)]">Preview</p>
                                    <span class="text-xs" [class.text-emerald-700]="(bulkImportPreviewResult?.errors?.length||0)===0" [class.text-red-700]="(bulkImportPreviewResult?.errors?.length||0)>0">
                                        {{ (bulkImportPreviewResult?.errors?.length||0)===0 ? 'Ready to apply' : 'Fix errors before applying' }}
                                    </span>
                                </div>

                                <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Rows</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.total_rows }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Create</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.will_create }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Update</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.will_update }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">New phases</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.will_create_phases }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Reserved</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.reserved_rows }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Leads create</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.leads_will_create }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-[color:var(--text-subtle)]">Leads skip</div>
                                        <div class="font-semibold text-[color:var(--foreground)]">{{ bulkImportPreviewResult.leads_will_skip }}</div>
                                    </div>
                                </div>

                                <div *ngIf="(bulkImportPreviewResult?.errors?.length || 0) > 0" class="space-y-2">
                                    <p class="font-semibold text-red-700">Errors</p>
                                    <ul class="space-y-1">
                                        <li *ngFor="let e of bulkImportPreviewResult.errors" class="text-red-700">
                                            Row {{ e.row }}<span *ngIf="e.field"> ({{ e.field }})</span>: {{ e.message }}
                                        </li>
                                    </ul>
                                </div>

                                <div *ngIf="(bulkImportPreviewResult?.warnings?.length || 0) > 0" class="space-y-2">
                                    <p class="font-semibold text-[color:var(--foreground)]">Warnings</p>
                                    <ul class="space-y-1">
                                        <li *ngFor="let w of bulkImportPreviewResult.warnings">
                                            Row {{ w.row }}: {{ w.message }}
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div *ngIf="bulkImportApplyResult?.ok" class="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800">
                                <p class="font-semibold">Import applied</p>
                                <p class="mt-1">
                                    Created: {{ bulkImportApplyResult.created_count }} · Updated: {{ bulkImportApplyResult.updated_count }} · Phases: {{ bulkImportApplyResult.phases_created }} · Leads created:
                                    {{ bulkImportApplyResult.leads_created_count }} · Leads skipped: {{ bulkImportApplyResult.leads_skipped_count }}
                                </p>
                            </div>
                        </ng-container>
                    </div>

                    <div class="flex flex-wrap items-center justify-between gap-2 border-t border-[color:var(--border)] px-5 py-4">
                        <button
                            type="button"
                            (click)="closeBulkImport()"
                            [disabled]="bulkImportPreviewing || bulkImportApplying"
                            class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <div class="flex flex-wrap items-center justify-end gap-2">
                            <button
                                *ngIf="bulkImportStep === 2"
                                type="button"
                                (click)="bulkImportStep = 1"
                                [disabled]="bulkImportPreviewing || bulkImportApplying"
                                class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] disabled:opacity-60"
                            >
                                Back
                            </button>
                            <button
                                *ngIf="bulkImportStep === 1"
                                type="button"
                                (click)="goToBulkImportStep2()"
                                [disabled]="!canProceedToBulkImportStep2() || bulkImportPreviewing || bulkImportApplying"
                                class="rounded-xl bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                *ngIf="bulkPriceEditorOpen"
                class="fixed inset-0 z-[125] flex items-end justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:items-center sm:p-6"
                (click)="closeBulkPriceEditor()"
            >
                <div
                    class="flex max-h-[calc(100vh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] shadow-2xl sm:max-h-[calc(100vh-3rem)]"
                    (click)="$event.stopPropagation()"
                >
                    <div class="flex items-center justify-between gap-4 border-b border-[color:var(--border)] px-5 py-4">
                        <div class="min-w-0">
                            <p class="text-lg font-semibold text-[color:var(--foreground)]">Bulk edit unit prices</p>
                            <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">The current filtered unit set is loaded here so prices can be updated quickly in one pass.</p>
                        </div>
                        <button
                            type="button"
                            (click)="closeBulkPriceEditor()"
                            [disabled]="bulkPriceSubmitting"
                            class="rounded-full bg-[color:var(--muted)] p-2 text-[color:var(--foreground)] hover:bg-[color:var(--hover)] disabled:opacity-60"
                        >
                            <lucide-icon name="x" class="h-4 w-4"></lucide-icon>
                        </button>
                    </div>

                    <div class="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                        <p *ngIf="bulkPriceError" class="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
                            {{ bulkPriceError }}
                        </p>

                        <div *ngIf="bulkPriceLoading" class="py-16 text-center text-[color:var(--muted-foreground)]">
                            <lucide-icon name="loader-2" class="mx-auto h-8 w-8 animate-spin text-[color:var(--primary)]"></lucide-icon>
                            <p class="mt-3 font-semibold text-[color:var(--foreground)]">Loading units…</p>
                        </div>

                        <div *ngIf="!bulkPriceLoading && bulkPriceItems.length === 0" class="py-16 text-center text-[color:var(--muted-foreground)]">
                            <p class="font-semibold text-[color:var(--foreground)]">No units matched the current filters.</p>
                        </div>

                        <div *ngIf="!bulkPriceLoading && bulkPriceItems.length" class="overflow-hidden rounded-2xl border border-[color:var(--border)]">
                            <div class="grid grid-cols-[minmax(0,1.8fr)_120px_170px] gap-3 border-b border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-subtle)]">
                                <span>Unit</span>
                                <span>Currency</span>
                                <span>Price</span>
                            </div>

                            <div class="max-h-[55vh] divide-y divide-[color:var(--border)] overflow-y-auto bg-[color:var(--card)]">
                                <div *ngFor="let item of bulkPriceItems" class="grid grid-cols-[minmax(0,1.8fr)_120px_170px] gap-3 px-4 py-3">
                                    <div class="min-w-0">
                                        <p class="truncate text-sm font-semibold text-[color:var(--foreground)]">{{ item.title || 'Untitled unit' }}</p>
                                        <p class="mt-1 truncate text-xs text-[color:var(--muted-foreground)]">
                                            {{ item.unitNumber ? ('Unit ' + item.unitNumber) : 'No unit number' }}
                                        </p>
                                    </div>
                                    <div class="flex items-center text-sm text-[color:var(--muted-foreground)]">
                                        {{ item.currency || '—' }}
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            [(ngModel)]="item.priceInput"
                                            [disabled]="bulkPriceSubmitting"
                                            inputmode="decimal"
                                            class="w-full rounded-xl border bg-[color:var(--card)] px-3 py-2 text-sm text-[color:var(--foreground)] focus:outline-none focus:border-[color:var(--ring)]"
                                            [ngClass]="bulkPriceInputInvalid(item) ? 'border-red-400' : 'border-[color:var(--input)]'"
                                        />
                                        <p class="mt-1 text-[11px] text-[color:var(--text-subtle)]">
                                            Current: {{ item.originalPrice !== null ? (item.originalPrice | number:'1.0-0') : '—' }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--border)] px-5 py-4">
                        <p class="text-sm text-[color:var(--muted-foreground)]">
                            {{ bulkPriceItems.length }} unit{{ bulkPriceItems.length === 1 ? '' : 's' }} loaded
                        </p>
                        <div class="flex flex-wrap gap-2">
                            <button
                                type="button"
                                (click)="closeBulkPriceEditor()"
                                [disabled]="bulkPriceSubmitting"
                                class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                (click)="saveBulkPrices()"
                                [disabled]="bulkPriceSubmitting || bulkPriceLoading || !bulkPriceHasChanges()"
                                class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60"
                            >
                                <lucide-icon [name]="bulkPriceSubmitting ? 'loader-2' : 'save'" class="h-4 w-4" [class.animate-spin]="bulkPriceSubmitting"></lucide-icon>
                                {{ bulkPriceSubmitting ? 'Saving…' : 'Save price changes' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    
